import React from 'react';
import { styled } from '@compiled/react';
import { graphql, useFragment } from 'react-relay';
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
// eslint-disable-next-line jira/restricted/@atlassian+jira-common-styles
import { gridSize } from '@atlassian/jira-common-styles/src';
import {
	BitbucketPendingConnectionBanner,
	Location,
} from '@atlassian/jira-devops-bitbucket-pending-connection-banner';
import { fg } from '@atlassian/jira-feature-gating';
import type { main_softwareCodePage_MainPage$key } from '@atlassian/jira-relay/src/__generated__/main_softwareCodePage_MainPage.graphql';
import type { ProjectContext } from '@atlassian/jira-router-resources-project-context';
import { isBitbucketPendingConnectionBannerEnabled } from '../../../feature-flags';
import { CodePageExperienceStart } from '../../../services';
import {
	VIEW_PR_LIST_EXPERIENCE_NAME,
	VIEW_REPO_LIST_EXPERIENCE_NAME,
} from '../../../services/experience-tracker';
import PullRequests from './pull-requests';
import Repositories from './repositories';
import { CodeTabWorkSuggestions } from './work-suggestions';

type MainPageProps = {
	mainPageData: main_softwareCodePage_MainPage$key;
	projectAri: string;
	onRefetch: () => void;
	isAdmin: boolean;
	projectContext: ProjectContext;
};

export const MainPage = ({
	mainPageData,
	projectAri,
	onRefetch,
	isAdmin,
	projectContext,
}: MainPageProps) => {
	const data = useFragment(
		graphql`
			fragment main_softwareCodePage_MainPage on Query {
				...main_softwareCodePage_Repositories
				...main_softwareCodePage_PullRequests
					@arguments(
						cloudId: $cloudId
						projectKey: $projectKey
						updatedFrom: $pullRequestsUpdatedFrom
						first: $numPullRequestsToFetch
					)
			}
		`,
		mainPageData,
	);

	return (
		<Wrapper>
			<CodePageExperienceStart
				experienceId={`codePageRepoList-${projectAri}`}
				experienceName={VIEW_REPO_LIST_EXPERIENCE_NAME}
			/>
			{isBitbucketPendingConnectionBannerEnabled() && (
				<Box xcss={bbcConnectionBannerWrapperStyles}>
					<BitbucketPendingConnectionBanner location={Location.CODE_IN_JIRA} />
				</Box>
			)}

			{fg('inxs-code-tab-suggestions-fe-flag') ? (
				<CodeTabWorkSuggestions />
			) : (
				<Repositories
					repositoriesData={data}
					isAdmin={isAdmin}
					onRefetch={onRefetch}
					projectContext={projectContext}
				/>
			)}
			<CodePageExperienceStart
				experienceId={`codePagePrList-${projectAri}`}
				experienceName={VIEW_PR_LIST_EXPERIENCE_NAME}
			/>
			<PullRequests pullRequestsData={data} onRefetch={onRefetch} />

			{fg('inxs-code-tab-suggestions-fe-flag') && (
				<Repositories
					repositoriesData={data}
					isAdmin={isAdmin}
					onRefetch={onRefetch}
					projectContext={projectContext}
				/>
			)}
		</Wrapper>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Wrapper = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	minWidth: `${gridSize * 10 + 225}px`,
	paddingLeft: token('space.500', '40px'),
	paddingRight: token('space.500', '40px'),
});

const bbcConnectionBannerWrapperStyles = xcss({
	marginBottom: 'space.200',
});
